if (!customElements.get('cts-customer-reels')) {
  class CTSCustomerReels extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.triggers = [...this.querySelectorAll('[data-reel-trigger]')];
      this.clips = [...this.querySelectorAll('[data-reel-template]')];
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.muted = false;
      this.triggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => this.open(index, trigger), { signal: this.events.signal });
      });
    }

    open(index, trigger) {
      document.querySelectorAll('cts-customer-reels').forEach((reels) => {
        if (reels !== this) reels.close?.();
      });
      if (!this.viewer) this.createViewer();
      const wasOpen = this.viewer.open;
      const previousIndex = this.index;
      const previousVideoId = this.stage.firstElementChild?.dataset.videoId;
      window.pauseAllMedia?.();
      this.opener = trigger;
      document.body.classList.add('cts-reels-open');
      if (!this.viewer.open) {
        if (this.mobile.matches) {
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.locked = true;
          this.viewer.showModal();
        } else {
          this.viewer.show();
        }
      }
      if (!wasOpen && !this.mobile.matches) this.watchStickyCart();
      this.showClip(index);
      if (!wasOpen) this.trackInteraction('opened');
      else if (index !== previousIndex) this.trackInteraction('changed', previousVideoId);
      this.viewer.querySelector('[data-reel-close]').focus({ preventScroll: true });
    }

    createViewer() {
      this.viewer = this.querySelector('[data-reel-viewer]').content.firstElementChild.cloneNode(true);
      document.body.append(this.viewer);
      const on = (element, event, handler) => element.addEventListener(event, handler, { signal: this.events.signal });
      this.stage = this.viewer.querySelector('.cts-reels__stage');
      this.spinner = this.viewer.querySelector('.cts-reels__spinner');
      this.progress = this.viewer.querySelector('.cts-reels__progress');
      this.playButton = this.viewer.querySelector('[data-reel-play]');
      this.soundButton = this.viewer.querySelector('[data-reel-sound]');
      this.message = this.viewer.querySelector('.cts-reels__message');
      this.resizeHandle = document.createElement('button');
      this.resizeHandle.type = 'button';
      this.resizeHandle.className = 'cts-reels__resize';
      this.resizeHandle.setAttribute('aria-label', 'Resize Reel window');
      this.resizeHandle.title = 'Drag to resize. Arrow keys adjust size. Double-click or press Home to reset.';
      this.resizeHandle.innerHTML = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M0 8L8 0M0 14L14 0M0 20L20 0"/></svg>';
      this.viewer.append(this.resizeHandle);
      on(this.resizeHandle, 'pointerdown', (event) => {
        if (this.mobile.matches || event.button !== 0 || this.resizeDrag) return;
        event.preventDefault();
        this.resizeHandle.focus({ preventScroll: true });
        this.resizeDrag = { id: event.pointerId, x: event.clientX, y: event.clientY, width: this.viewer.getBoundingClientRect().width };
        this.resizeHandle.setPointerCapture(event.pointerId);
        this.viewer.classList.add('cts-reels-viewer--resizing');
      });
      on(this.resizeHandle, 'pointermove', (event) => {
        if (event.pointerId !== this.resizeDrag?.id) return;
        const { x, y, width } = this.resizeDrag;
        const ratio = 16 / 9;
        this.resizeViewer(width + ((x - event.clientX) + ratio * (y - event.clientY)) / (1 + ratio * ratio));
      });
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((event) => on(this.resizeHandle, event, () => this.endResize()));
      on(this.resizeHandle, 'dblclick', () => this.viewer.style.removeProperty('--cts-reels-width'));
      on(this.resizeHandle, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        if (event.key === 'Home') this.viewer.style.removeProperty('--cts-reels-width');
        else this.resizeViewer(this.viewer.getBoundingClientRect().width + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? 16 : -16));
      });
      on(window, 'resize', () => { this.endResize(); this.updateCartClearance(); });
      on(window, 'blur', () => { this.endResize(); this.scrubbing = false; });
      on(this.viewer.querySelector('[data-reel-close]'), 'click', () => this.close());
      on(this.viewer.querySelector('[data-reel-prev]'), 'click', () => this.navigate(-1));
      on(this.viewer.querySelector('[data-reel-next]'), 'click', () => this.navigate(1));
      on(this.playButton, 'click', () => this.togglePlayback());
      on(this.soundButton, 'click', () => {
        this.muted = !this.video.muted;
        this.video.muted = this.muted;
        this.updateSound();
      });
      on(this.progress, 'pointerdown', () => { this.scrubbing = true; });
      ['pointerup', 'pointercancel'].forEach((event) => on(window, event, () => { this.scrubbing = false; }));
      on(this.progress, 'change', () => { this.scrubbing = false; });
      on(this.progress, 'input', () => {
        if (Number.isFinite(this.video.duration)) this.video.currentTime = this.video.duration * this.progress.value / 100;
      });
      on(this.viewer, 'cancel', (event) => { event.preventDefault(); this.close(); });
      on(this.viewer, 'keydown', (event) => {
        if (event.target === this.progress) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.navigate(event.key === 'ArrowDown' ? 1 : -1);
        }
      });
      on(document, 'keydown', (event) => {
        if (event.key !== 'Escape' || event.defaultPrevented || !this.viewer.open) return;
        // Let a modal above the non-modal desktop player handle its own Escape.
        const otherModal = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some((element) =>
          element !== this.viewer && !element.closest('details:not([open])') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        if (otherModal) return;
        event.preventDefault();
        this.close();
      });
      on(this.stage, 'click', () => this.togglePlayback());
      on(this.stage, 'touchstart', (event) => {
        this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      });
      on(this.stage, 'touchend', (event) => {
        if (!this.touch || !this.mobile.matches) return;
        const deltaY = event.changedTouches[0].clientY - this.touch.y;
        const deltaX = event.changedTouches[0].clientX - this.touch.x;
        this.touch = null;
        if (Math.abs(deltaY) > 60 && Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
          this.ignoreClickUntil = Date.now() + 400;
          this.navigate(deltaY < 0 ? 1 : -1);
        }
      });
      on(this.stage, 'touchcancel', () => { this.touch = null; });
      on(document, 'visibilitychange', () => { if (document.hidden) this.video?.pause(); });
      on(this.mobile, 'change', () => this.updateViewerMode());
    }

    updateViewerMode() {
      if (!this.viewer.open) return;
      const wasPlaying = this.video && !this.video.paused;
      const focused = this.viewer.contains(document.activeElement) ? document.activeElement : null;
      this.endResize();
      this.stopWatchingStickyCart();
      this.viewer.close();
      if (this.mobile.matches) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.locked = true;
        this.viewer.showModal();
      } else {
        if (this.locked) document.body.style.overflow = this.previousOverflow;
        this.locked = false;
        this.viewer.show();
        this.watchStickyCart();
      }
      focused?.focus({ preventScroll: true });
      if (wasPlaying && this.video.paused) this.play();
    }

    showClip(index) {
      if (index < 0 || index >= this.clips.length) return;
      this.video?.pause();
      this.clipEvents?.abort();
      this.clipEvents = new AbortController();
      this.index = index;
      const clip = this.clips[index].content.cloneNode(true);
      const nextVideo = clip.querySelector('video');
      if (this.video) {
        // Keep the media element's playback permission across successive Reels.
        this.video.replaceChildren(...nextVideo.childNodes);
        this.video.poster = nextVideo.poster;
        nextVideo.replaceWith(this.video);
      } else {
        this.video = nextVideo;
      }
      this.stage.replaceChildren(clip);
      this.scrubbing = false;
      this.video.muted = this.muted;
      this.video.loop = this.clips.length === 1;
      this.progress.value = 0;
      this.progress.setAttribute('aria-valuetext', '0 seconds');
      this.touch = null;
      this.message.hidden = true;
      this.hideSpinner();
      this.viewer.setAttribute('aria-label', `Customer Reel by ${this.stage.querySelector('[data-reel-customer]').textContent}`);
      this.viewer.querySelector('[data-reel-prev]').disabled = index === 0;
      this.viewer.querySelector('[data-reel-next]').disabled = index === this.clips.length - 1;
      this.viewer.querySelector('[data-reel-count]').textContent = `${index + 1} / ${this.clips.length}`;
      this.updateSound();
      this.updatePlayback();
      const on = (event, handler) => this.video.addEventListener(event, handler, { signal: this.clipEvents.signal });
      on('play', () => this.updatePlayback());
      on('pause', () => { this.updatePlayback(); this.hideSpinner(); });
      on('timeupdate', () => {
        if (!this.scrubbing && Number.isFinite(this.video.duration) && this.video.duration > 0) {
          this.progress.value = this.video.currentTime / this.video.duration * 100;
          this.progress.setAttribute('aria-valuetext', `${Math.floor(this.video.currentTime)} of ${Math.ceil(this.video.duration)} seconds`);
        }
      });
      on('ended', () => {
        this.hideSpinner();
        if (this.index < this.clips.length - 1) this.showClip(this.index + 1);
      });
      const handleMediaError = () => {
        this.video.pause();
        this.showPlaybackError();
        this.updatePlayback();
      };
      on('error', handleMediaError);
      // A failed <source> does not bubble an error to <video> in every browser.
      const sources = [...this.video.querySelectorAll('source')];
      let failedSources = 0;
      sources.forEach((source) => source.addEventListener('error', () => {
        failedSources += 1;
        if (failedSources === sources.length) handleMediaError();
      }, { once: true, signal: this.clipEvents.signal }));
      this.video.load();
      this.play();
      this.startWatchdog();
    }

    watchStickyCart() {
      const watch = () => {
        const cart = document.querySelector('.zrx-sticky-atc-bar');
        if (cart === this.stickyCart) return;
        this.cartSizeObserver?.disconnect();
        this.cartStateObserver?.disconnect();
        this.stickyCart = cart;
        if (cart) {
          this.cartSizeObserver = new ResizeObserver(() => this.updateCartClearance());
          this.cartSizeObserver.observe(cart);
          this.cartStateObserver = new MutationObserver(() => this.updateCartClearance());
          this.cartStateObserver.observe(cart, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        }
        this.updateCartClearance();
      };
      watch();
      // The app can insert or replace its bar after the viewer has opened.
      this.cartMountObserver = new MutationObserver((records) => {
        if (this.cartWatchFrame) return;
        const changed = records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
          node.nodeType === 1 && (node.matches('.zrx-sticky-atc-bar') || node.querySelector('.zrx-sticky-atc-bar'))));
        if (!changed) return;
        this.cartWatchFrame = requestAnimationFrame(() => { this.cartWatchFrame = null; watch(); });
      });
      this.cartMountObserver.observe(document.body, { childList: true, subtree: true });
      this.updateCartClearance();
    }

    stopWatchingStickyCart() {
      this.cartSizeObserver?.disconnect();
      this.cartStateObserver?.disconnect();
      this.cartMountObserver?.disconnect();
      cancelAnimationFrame(this.cartWatchFrame);
      this.cartWatchFrame = null;
      this.stickyCart = null;
    }

    updateCartClearance() {
      if (!this.viewer?.open || this.mobile.matches) return;
      const cart = this.stickyCart;
      let bottom = 24;
      if (cart?.classList.contains('zrx-sticky-atc-show') && cart.classList.contains('zrx-sticky-atc-bar-bottom')) {
        const style = getComputedStyle(cart);
        if (style.display !== 'none' && style.visibility !== 'hidden') {
          bottom = Math.max(24, cart.getBoundingClientRect().height + Math.max(0, parseFloat(style.bottom) || 0) + 12);
        }
      }
      this.viewerBottom = bottom;
      this.viewer.style.setProperty('--cts-reels-bottom', `${bottom}px`);
    }

    resizeViewer(width) {
      if (this.mobile.matches) return;
      const maxWidth = Math.min(window.innerWidth - 48, (window.innerHeight - (this.viewerBottom || 24) - 24) * 9 / 16);
      const minWidth = Math.min(240, maxWidth);
      this.viewer.style.setProperty('--cts-reels-width', `${Math.max(minWidth, Math.min(maxWidth, width))}px`);
    }

    endResize() {
      const pointerId = this.resizeDrag?.id;
      this.resizeDrag = null;
      this.viewer?.classList.remove('cts-reels-viewer--resizing');
      if (pointerId !== undefined && this.resizeHandle.hasPointerCapture(pointerId)) this.resizeHandle.releasePointerCapture(pointerId);
    }

    navigate(direction) {
      if (!this.viewer?.open) return;
      const nextIndex = this.index + direction;
      if (nextIndex < 0 || nextIndex >= this.clips.length || nextIndex === this.index) return;
      const previousVideoId = this.stage.firstElementChild?.dataset.videoId;
      this.showClip(nextIndex);
      this.trackInteraction('changed', previousVideoId);
    }

    trackInteraction(action, previousVideoId) {
      this.dispatchEvent(new CustomEvent('cts:reels-interaction', {
        bubbles: true,
        detail: {
          action,
          videoElement: this.stage.firstElementChild,
          video_index: this.index + 1,
          ...(action === 'opened' ? { reel_count: this.clips.length } : { previous_video_id: previousVideoId }),
        },
      }));
    }

    play() {
      window.pauseAllMedia?.();
      const video = this.video;
      const signal = this.clipEvents.signal;
      const attempt = video.play();
      attempt?.catch((error) => {
        if (signal.aborted || this.video !== video || !this.viewer?.open || error.name === 'AbortError') return;
        if (error.name === 'NotAllowedError') {
          this.hideSpinner();
          this.message.textContent = 'Tap play to watch this Reel.';
          this.message.hidden = false;
        } else {
          this.showPlaybackError();
        }
        this.updatePlayback();
      });
    }

    startWatchdog() {
      this.stopWatchdog();
      this.lastTime = this.video.currentTime;
      this.lastProgressAt = performance.now();
      this.watchdog = setInterval(() => this.checkProgress(), 100);
    }

    stopWatchdog() {
      clearInterval(this.watchdog);
      this.watchdog = null;
    }

    // Safari's native HLS player fires `seeked` before the data arrives and skips `waiting`, so watch real progress instead of events.
    checkProgress() {
      const video = this.video;
      if (!video || !this.viewer?.open) return;
      const idle = video.paused || video.ended || !this.message.hidden;
      if (idle || video.currentTime !== this.lastTime) {
        this.lastTime = video.currentTime;
        this.lastProgressAt = performance.now();
        this.hideSpinner();
        return;
      }
      if (this.spinner && performance.now() - this.lastProgressAt >= 500) this.spinner.hidden = false;
    }

    hideSpinner() {
      if (this.spinner) this.spinner.hidden = true;
    }

    showPlaybackError() {
      this.hideSpinner();
      this.message.textContent = 'This Reel could not load. Close it and try again, or choose another Reel.';
      this.message.hidden = false;
    }

    togglePlayback() {
      if (Date.now() < (this.ignoreClickUntil || 0)) return;
      this.message.hidden = true;
      if (this.video.paused) this.play();
      else this.video.pause();
    }

    updatePlayback() {
      this.playButton.toggleAttribute('data-paused', this.video.paused);
      this.playButton.setAttribute('aria-label', this.video.paused ? 'Play Reel' : 'Pause Reel');
    }

    updateSound() {
      this.soundButton.toggleAttribute('data-muted', this.video.muted);
      this.soundButton.setAttribute('aria-label', this.video.muted ? 'Unmute Reel' : 'Mute Reel');
    }

    close() {
      if (!this.viewer?.open) return;
      this.endResize();
      this.stopWatchingStickyCart();
      this.scrubbing = false;
      this.video?.pause();
      this.clipEvents?.abort();
      this.stopWatchdog();
      this.hideSpinner();
      this.stage.replaceChildren();
      this.viewer.close();
      document.body.classList.remove('cts-reels-open');
      if (this.locked) {
        document.body.style.overflow = this.previousOverflow;
        this.locked = false;
      }
      this.opener?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.close();
      this.events?.abort();
      this.viewer?.remove();
      this.events = null;
      this.viewer = null;
    }
  }
  customElements.define('cts-customer-reels', CTSCustomerReels);
}
